
import { Badge, Group, Image, Text } from '@mantine/core'
import { MRT_ColumnDef } from 'mantine-react-table'
import React, { memo, useCallback, useMemo, useState } from 'react'
import CustomTooltip from 'src/components/CustomComponents/CustomTooltip'
import BookUniViniPageList from 'src/containers/BookUniVini'
import { useTabletLayout, useTouchDevice } from 'src/hooks/useTouchDevice'
import { size } from 'src/variable/constant'
import { DialogContent, Dialog } from '@mui/material'

export interface ChapterContent {
	audio?: unknown[]
	docUrl?: string
	language?: string
}

export interface Chapter {
	id: string
	title: string
	description: string
	cover_image: string
	original_doc_url: string | null
	audio_url: string | null
	content?: ChapterContent[]
}

interface BookUniViniRow {
	chapters?: Chapter[]
	language?: string[]
	book_duration?: { language: string; duration: number }[]
	review_summary?: { total_reviews?: number }
	total_view_count?: number
}

const TABLET_HIDDEN_BOOK_COLUMNS = new Set([
	'author',
	'summary',
	'downloaded',
	'review',
	'view',
])

const CHAPTER_HIDDEN_COLUMNS = new Set(['description', 'original_doc_url'])

const ellipsisStyle = (maxWidth: number): React.CSSProperties => ({
	maxWidth,
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
})

const StatCell = ({ value }: { value: string | number }) => (
	<Group spacing={4} sx={{ justifyContent: 'center' }}>
		<Text size="sm" weight={700} color="#1f7a44">
			{value}
		</Text>
	</Group>
)

const getDocFileCount = (chapters: Chapter[] = []) =>
	chapters.filter(
		(ch) =>
			Boolean(ch.original_doc_url) ||
			ch.content?.some((item) => Boolean(item.docUrl)),
	).length

const getAudioDisplay = (book: BookUniViniRow) => {
	const audioCount = book.book_duration?.length ?? 0
	const totalLang = book.language?.length ?? 0
	if (totalLang === 0) return String(audioCount)
	return `${audioCount}/${totalLang}`
}

const getReviewCount = (book: BookUniViniRow) =>
	book.review_summary?.total_reviews ?? 0

const getViewCount = (book: BookUniViniRow) =>
	book.total_view_count ?? 0

function BooksUniViniPage() {
	const isTablet = useTabletLayout()
	const isTouchDevice = useTouchDevice()
	const coverHeight = isTablet ? 64 : 100

	const renderTextCell = useCallback((value: string | null | undefined, maxWidth: number) => {
		const text = value?.trim() || '-'
		if (isTouchDevice) {
			return <div style={ellipsisStyle(maxWidth)}>{text}</div>
		}
		return <CustomTooltip label={text} maxWidth={maxWidth} />
	}, [isTouchDevice])

	const columns: MRT_ColumnDef[] = useMemo(() => {
		const allColumns: MRT_ColumnDef[] = [
			{
				accessorKey: 'no',
				header: 'No',
				size: size.zero,
			},
			{
				accessorKey: 'title',
				header: 'Title',
				size: size.zero,
				maxSize: size.text,
				Cell: ({ cell }) => renderTextCell(cell.getValue<string>(), size.text),
			},
			{
				accessorKey: 'cover_image',
				header: 'Cover',
				size: size.zero,
				Cell: ({ cell }) => {
					const [open, setOpen] = useState(false)
					const imageUrl = cell.getValue<string>()

					return (
						<>
							<Image
								src={imageUrl}
								height={imageUrl ? coverHeight : 0}
								width="auto"
								sx={{ maxWidth: coverHeight }}
								fit="contain"
								onClick={() => setOpen(true)}
							/>
							<Dialog open={open} onClose={() => setOpen(false)} maxWidth="md">
								<DialogContent>
									<img
										src={imageUrl}
										alt="Cover"
										style={{ width: '100%', height: 'auto' }}
									/>
								</DialogContent>
							</Dialog>
						</>
					)
				},
			},
			{
				accessorKey: 'author',
				header: 'Author',
				size: size.zero,
				maxSize: size.text,
				Cell: ({ cell }) => renderTextCell(cell.getValue<string>(), size.mediumS),
			},
			{
				accessorKey: 'level',
				header: 'Level',
				size: size.zero,
				maxSize: size.text,
				Cell: ({ cell }) => {
					const level = (cell.getValue<string>() || '').toUpperCase()
					if (!level) return <>-</>
					return (
						<Badge
							variant="light"
							color="teal"
							radius="sm"
							sx={{ textTransform: 'uppercase', fontWeight: 700 }}
						>
							{level}
						</Badge>
					)
				},
			},
			{
				accessorKey: 'category',
				header: 'Category',
				size: size.zero,
				maxSize: size.text,
				Cell: ({ cell }) => {
					const value = cell.getValue<string[]>()
					if (!value) return <>No</>
					if (isTouchDevice) {
						return <div style={ellipsisStyle(size.mediumS)}>{value.join(', ')}</div>
					}
					return (
						<>
							{value.map((item, index) => (
								<CustomTooltip key={index} label={item} maxWidth={size.mediumS} />
							))}
						</>
					)
				},
			},
			{
				accessorKey: 'summary',
				header: 'Summary',
				size: size.zero,
				maxSize: size.text,
				Cell: ({ cell }) => renderTextCell(cell.getValue<string>(), size.mediumS),
			},
			{
				accessorKey: 'chapters',
				header: 'Chapters',
				size: size.zero,
				maxSize: size.text,
				Cell: ({ cell }) => {
					const chapters = cell.getValue<Chapter[]>() ?? []
					return (
						<Group spacing={4} sx={{ justifyContent: 'center' }}>
							<Text size="sm" weight={700} color="#1f7a44">
								{chapters.length}
							</Text>
						</Group>
					)
				},
			},
			{
				accessorKey: 'chapters',
				id: 'doc_file',
				header: 'Doc',
				size: size.zero,
				maxSize: size.text,
				Cell: ({ row }) => {
					const book = row.original as BookUniViniRow
					return <StatCell value={getDocFileCount(book.chapters)} />
				},
			},
			{
				accessorKey: 'book_duration',
				id: 'audio',
				header: 'Audio',
				size: size.zero,
				maxSize: size.text,
				Cell: ({ row }) => {
					const book = row.original as BookUniViniRow
					return <StatCell value={getAudioDisplay(book)} />
				},
			},
			{
				accessorKey: 'downloaded',
				id: 'downloaded',
				header: 'Downloaded',
				size: size.zero,
				maxSize: size.text,
				Cell: () => (
					<Group spacing={4} sx={{ justifyContent: 'center' }}>
						<Text size="sm" weight={700} color="#1f7a44">
							No API DATA
						</Text>
					</Group>
				),
			},
			{
				accessorKey: 'review_summary',
				id: 'review',
				header: 'Review',
				size: size.zero,
				maxSize: size.text,
				Cell: ({ row }) => {
					const book = row.original as BookUniViniRow
					return <StatCell value={getReviewCount(book)} />
				},
			},
			{
				accessorKey: 'total_view_count',
				id: 'view',
				header: 'View',
				size: size.zero,
				maxSize: size.text,
				Cell: ({ row }) => {
					const book = row.original as BookUniViniRow
					return <StatCell value={getViewCount(book)} />
				},
			},
			{
				accessorKey: 'updated_at',
				header: 'Updated',
				size: size.zero,
				maxSize: size.text,
			},
		]

		if (!isTablet) return allColumns

		return allColumns.filter((column) => {
			const key = String(column.id ?? column.accessorKey ?? '')
			return !TABLET_HIDDEN_BOOK_COLUMNS.has(key)
		})
	}, [coverHeight, isTablet, isTouchDevice, renderTextCell])

	const chapterColumns: MRT_ColumnDef[] = useMemo(() => {
		const allColumns: MRT_ColumnDef[] = [
			{
				accessorKey: 'cover_image',
				header: 'Image',
				size: size.zero,
				Cell: ({ cell }) => {
					const [open, setOpen] = useState(false)
					const imageUrl = cell.getValue<string>()

					return (
						<>
							<Image
								src={imageUrl}
								height={imageUrl ? coverHeight : 0}
								width="auto"
								sx={{ maxWidth: coverHeight }}
								fit="contain"
								onClick={() => setOpen(true)}
							/>
							<Dialog open={open} onClose={() => setOpen(false)} maxWidth="md">
								<DialogContent>
									<img
										src={imageUrl}
										alt="Cover"
										style={{ width: '100%', height: 'auto' }}
									/>
								</DialogContent>
							</Dialog>
						</>
					)
				},
			},
			{
				accessorKey: 'title',
				header: "Chapter's Name",
				size: size.zero,
				maxSize: size.text,
				Cell: ({ cell }) => renderTextCell(cell.getValue<string>(), size.text),
			},
			{
				accessorKey: 'description',
				id: 'description',
				header: "Description's Chapter",
				size: size.zero,
				maxSize: size.text,
				Cell: ({ cell }) => renderTextCell(cell.getValue<string>(), size.mediumS),
			},
			{
				accessorKey: 'approved_status',
				header: "Status's Chapter",
				size: size.zero,
				maxSize: size.text,
				Cell: ({ cell }) => {
					const rawStatus = (cell.getValue<string>() || '').toLowerCase()
					const statusLabel =
						rawStatus === 'approved'
							? 'Approved'
							: rawStatus === 'inprogress'
								? 'In Progress'
								: rawStatus === 'rejected'
									? 'Rejected'
									: cell.getValue<string>() || '-'
					const statusColor =
						rawStatus === 'approved'
							? 'green'
							: rawStatus === 'inprogress'
								? 'yellow'
								: rawStatus === 'rejected'
									? 'red'
									: 'gray'
					return (
						<Badge variant="light" color={statusColor} radius="sm" sx={{ fontWeight: 700 }}>
							{statusLabel}
						</Badge>
					)
				},
			},
			{
				accessorKey: 'original_doc_url',
				id: 'original_doc_url',
				header: "Original Doc Url's Chapter",
				size: size.zero,
				maxSize: size.text,
				Cell: ({ cell }) => {
					if (!cell.getValue<string>()) return <>No Data</>
					return (
						<a href={cell.getValue<string>()} target="_blank" rel="noreferrer">
							{renderTextCell(cell.getValue<string>(), size.mediumS)}
						</a>
					)
				},
			},
		]

		return allColumns.filter((column) => {
			const key = String(column.id ?? column.accessorKey ?? '')
			return !CHAPTER_HIDDEN_COLUMNS.has(key)
		})
	}, [coverHeight, isTouchDevice, renderTextCell])

	return (
		<BookUniViniPageList columns={columns} fields={[]} chapterColumns={chapterColumns} />
	)
}

export default memo(BooksUniViniPage)
